import React, { useState, useEffect, useRef } from 'react';
import { Upload, FileText, Save, Eye, EyeOff, AlertCircle, CheckCircle, Loader, LogOut, User, Home, ArrowLeft, Edit, Trash2 } from 'lucide-react';

// API Configuration
const API_BASE = '/api';




// ============================================================================
// AUTH SERVICE
// ============================================================================
const AuthService = {
  login: async (email, password) => {
    const res = await fetch(`${API_BASE}/auth/login`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ email, password }),
    });
  
    const data = await res.json();
  
    if (!res.ok) throw new Error(data.detail || "Login failed");
  
    localStorage.setItem("auth_token", data.access_token);
    localStorage.setItem("user", JSON.stringify(data.user));
    console.log("TOKEN AFTER SAVE:", localStorage.getItem("auth_token"));

    return data;
  },
  

  signup: async (email, password, name) => {
    const res = await fetch(`${API_BASE}/auth/signup`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ email, password, name }),
    });

    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.detail || "Signup failed");
    }

    return await res.json();
  },

  logout: () => {
    localStorage.removeItem("auth_token");
    localStorage.removeItem("user");
  },

  getToken: () => localStorage.getItem("auth_token"),

  getUser: () => {
    const user = localStorage.getItem("user");
    if (!user || user === "undefined") return null;
    return JSON.parse(user);
  },
  isAuthenticated: () =>
    !!localStorage.getItem("auth_token"),
};

const authHeader = () => {
  const token = localStorage.getItem("auth_token");
  if (!token) return {};
  return { Authorization: `Bearer ${token}` };
};





// ============================================================================
// API SERVICE
// ============================================================================
const API = {
  uploadDocument: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('auto_extract', 'true');
    
    const res = await fetch(`${API_BASE}/documents/upload`, {
      method: 'POST',
      headers:authHeader(),
      body: formData
    });
    
    if (!res.ok) throw new Error('Upload failed');
    return res.json();
  },

  deleteDocument: async (documentId) => {
    const res = await fetch(`${API_BASE}/documents/${documentId}`, {
      method: 'DELETE',
      headers: authHeader()
    });
    if (!res.ok) throw new Error('Failed to delete document');
    return res.json();
  },
  
  getExtraction: async (extractionId) => {
    const res = await fetch(`${API_BASE}/extractions/${extractionId}`,{
      headers: authHeader()
    });
    if (!res.ok) throw new Error('Failed to get extraction');
    return res.json();
  },
  
  pollExtractionStatus: async (documentId) => {
    const res = await fetch(`${API_BASE}/documents/${documentId}`, {
   
      headers:authHeader()
      
    });
    if (!res.ok) throw new Error('Failed to get document');
    const doc = await res.json();
    
    if (doc.current_extraction_id) {
      return await API.getExtraction(doc.current_extraction_id);
    }
    return null;
  },
  
  updateField: async (extractionId, fieldId, newValue) => {
    const res = await fetch(`${API_BASE}/extractions/${extractionId}/fields/${fieldId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json',...authHeader() },
      body: JSON.stringify({ field_value: newValue })
    });
    
    if (!res.ok) throw new Error('Failed to update field');
    return res.json();
  },

  deleteField: async (extractionId, fieldId) => {
    const res = await fetch(
        `${API_BASE}/extractions/${extractionId}/fields/${fieldId}`,
        { method: 'DELETE', headers: authHeader() }
    );
    if (!res.ok) throw new Error('Failed to delete field');
    return res.json();
},
  
  saveExtraction: async (extractionId) => {
    const res = await fetch(`${API_BASE}/extractions/${extractionId}/finalize`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' ,...authHeader()},
      body: JSON.stringify({ confirm: true })
    });
    
    if (!res.ok) throw new Error('Failed to save extraction');
    return res.json();
  },
  
  getProcessedImage: (documentId, pageNumber = 1) => {
    return `${API_BASE}/documents/${documentId}/processed-image/${pageNumber}`;
  },
  
  // Fixed API methods
  getAllDocuments: async () => {
    const res = await fetch(`${API_BASE}/documents`,{headers:authHeader()});
    if (!res.ok) throw new Error('Failed to get documents');
    const data = await res.json();
    console.log('Documents API response:', data); // Debug log
    // Handle both array and object responses
    return Array.isArray(data) ? data : (data.items || data.documents || []);
  },
  
  getSavedExtractions: async () => {
    const res = await fetch(`${API_BASE}/extractions?status=finalized` ,{
      
      headers:authHeader(),
      
    });
    if (!res.ok) throw new Error('Failed to get saved extractions');
    const data = await res.json();
    // Handle both array and object responses
    return Array.isArray(data) ? data : (data.extractions || []);
  }
};

// ============================================================================
// AUTH SCREEN
// ============================================================================
const AuthScreen = ({ onLogin }) => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleAuth = async () => {
    setError('');
    setLoading(true);

    try {
      if (isLogin) {
        // Login flow - auto-login
        const result = await AuthService.login(email, password);
        onLogin(result.user);
      } else {
        // ✅ Signup flow - DON'T auto-login
        const result = await AuthService.signup(email, password, name);
        
        // Show success message
        setError(''); // Clear any errors
        alert(`✅ ${result.message}\n\nPlease check ${result.email} for the verification link.`);
        
        // Switch to login view
        setIsLogin(true);
        setPassword(''); // Clear password for security
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md">
        <div className="text-center mb-8">
          <FileText className="w-16 h-16 text-indigo-600 mx-auto mb-4" />
          <h1 className="text-3xl font-bold text-gray-800">InvoSense AI</h1>
          <p className="text-gray-600 mt-2">OCR Document Intelligence</p>
        </div>

        <div className="space-y-4">
          {!isLogin && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none"
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none"
            />
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          <button
            onClick={handleAuth}
            disabled={loading}
            className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-medium py-3 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <Loader className="w-5 h-5 animate-spin" />
                Processing...
              </>
            ) : (
              isLogin ? 'Sign In' : 'Sign Up'
            )}
          </button>
        </div>

        <div className="mt-6 text-center">
          <button
            onClick={() => setIsLogin(!isLogin)}
            className="text-indigo-600 hover:text-indigo-700 text-sm font-medium"
          >
            {isLogin ? "Don't have an account? Sign up" : "Already have an account? Sign in"}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// PROCESSED VIEW - Shows all uploaded documents
// ============================================================================
const ProcessedView = ({ onSelectDocument, onDeleteDocument }) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const token = localStorage.getItem("auth_token");
    if (!token) {
      setLoading(false);
      return;
    }
  
    const fetchDocuments = async () => {
      try {
        const docs = await API.getAllDocuments();
        console.log('Fetched documents:', docs);
        setDocuments(Array.isArray(docs) ? docs : []);
      } catch (err) {
        console.error(err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
  
    fetchDocuments();
  }, []);

  const handleDelete = async (docId) => {
    if (window.confirm('Delete this document? This cannot be undone.')) {
      try {
        await onDeleteDocument(docId);
        setDocuments(prev => prev.filter(d => d.id !== docId));
      } catch (err) {
        console.error('Delete error:', err);
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader className="w-12 h-12 text-indigo-600 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
        <AlertCircle className="w-5 h-5" />
        <span>{error}</span>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-lg p-6">
      <h2 className="text-2xl font-bold text-gray-800 mb-6">Processed Documents</h2>
      
      {documents.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <FileText className="w-16 h-16 mx-auto mb-4 text-gray-400" />
          <p>No documents uploaded yet</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {documents.map((doc) => {
            const normalizedStatus = typeof doc.status === 'string' 
              ? doc.status.toUpperCase() 
              : doc.status?.name?.toUpperCase() || 'UNKNOWN';
            
            const hasExtraction = !!doc.current_extraction_id;
            const isFinalized = doc.is_finalized || false;
            const isClickable = hasExtraction && normalizedStatus === 'COMPLETED';
            
            return (
              <div
                key={doc.id}
                className="border rounded-lg p-4 transition-all relative group"
              >
                {/* Delete Button */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(doc.id);
                  }}
                  className="absolute top-2 right-2 p-1.5 text-red-600 hover:bg-red-50 rounded opacity-0 group-hover:opacity-100 transition-opacity z-10"
                  title="Delete document"
                >
                  <Trash2 className="w-4 h-4" />
                </button>

                {/* Clickable Card Content */}
                <div
                  onClick={() => isClickable && onSelectDocument(doc)}
                  className={isClickable ? 'cursor-pointer' : 'cursor-not-allowed'}
                >
                  <div className="mb-3">  {/* ✅ Simplified - no flex justify-between */}
                    <div className="pr-8">  {/* ✅ Keep padding-right for delete button space */}
                      <h3 className="font-medium text-gray-800 truncate" title={doc.filename}>
                        {doc.filename}
                      </h3>
                      <p className="text-xs text-gray-500 mt-1">
                        {new Date(doc.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2 flex-wrap">
                    {/* Status Badge */}
                    <span className={`px-2 py-1 text-xs rounded ${
                      normalizedStatus === 'COMPLETED' 
                        ? 'bg-green-100 text-green-700'
                        : normalizedStatus === 'PROCESSING'
                        ? 'bg-blue-100 text-blue-700'
                        : normalizedStatus === 'FAILED'
                        ? 'bg-red-100 text-red-700'
                        : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      {normalizedStatus}
                    </span>
                    
                    {/* Extraction Status Badge */}
                    {normalizedStatus === 'COMPLETED' && hasExtraction && (
                      <span className={`px-2 py-1 text-xs rounded flex items-center gap-1 ${
                        isFinalized 
                          ? 'bg-purple-100 text-purple-700'
                          : 'bg-blue-100 text-blue-700'
                      }`}>
                        {isFinalized ? (
                          <>
                            <CheckCircle className="w-3 h-3" />
                            Saved
                          </>
                        ) : (
                          <>
                            <Edit className="w-3 h-3" />
                            Extracted
                          </>
                        )}
                      </span>
                    )}
                    
                    {normalizedStatus === 'COMPLETED' && !hasExtraction && (
                      <span className="px-2 py-1 text-xs bg-orange-100 text-orange-700 rounded">
                        Not Extracted
                      </span>
                    )}
                    
                    {normalizedStatus === 'PROCESSING' && (
                      <span className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded flex items-center gap-1">
                        <Loader className="w-3 h-3 animate-spin" />
                        Processing...
                      </span>
                    )}
                    
                    {normalizedStatus === 'FAILED' && (
                      <span className="px-2 py-1 text-xs bg-red-100 text-red-700 rounded flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        Failed
                      </span>
                    )}
                  </div>
                  
                  {/* Helper text */}
                  {isClickable && !isFinalized && (
                    <p className="text-xs text-blue-600 mt-2">
                      Click to edit and save
                    </p>
                  )}
                  {isClickable && isFinalized && (
                    <p className="text-xs text-purple-600 mt-2">
                      Click to view (read-only)
                    </p>
                  )}
                  {!isClickable && normalizedStatus === 'COMPLETED' && !hasExtraction && (
                    <p className="text-xs text-gray-500 mt-2">
                      Upload again with auto-extract enabled
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
// ============================================================================
// SAVED VIEW - Shows all finalized extractions
// ============================================================================
const SavedView = ({ onSelectExtraction }) => {
  const [extractions, setExtractions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchExtractions = async () => {
      try {
        const exts = await API.getSavedExtractions();
        console.log('Fetched extractions:', exts); // Debug log
        setExtractions(Array.isArray(exts) ? exts : []);
      } catch (err) {
        console.error('Error fetching extractions:', err);
        setError('Failed to load saved extractions: ' + err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchExtractions();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader className="w-12 h-12 text-indigo-600 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
        <AlertCircle className="w-5 h-5" />
        <span>{error}</span>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-lg p-6">
      <h2 className="text-2xl font-bold text-gray-800 mb-6">Saved Extractions</h2>
      
      {extractions.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <Save className="w-16 h-16 mx-auto mb-4 text-gray-400" />
          <p>No saved extractions yet</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {extractions.map((ext) => (
            <div
              key={ext.id}
              onClick={() => onSelectExtraction(ext)}
              className="border border-gray-200 rounded-lg p-4 hover:border-green-500 hover:shadow-md transition-all cursor-pointer"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <h3 className="font-medium text-gray-800">Extraction #{ext.id}</h3>
                  <p className="text-xs text-gray-500 mt-1">
                    {new Date(ext.created_at).toLocaleDateString()}
                  </p>
                </div>
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
              </div>
              
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Fields:</span>
                  <span className="font-medium text-gray-800">{ext.fields?.length || 0}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded">
                    {ext.status}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// EXTRACTION VIEWER - Shows extraction details
// ============================================================================
const ExtractionViewer = ({ extraction, onBack, showSaveButton = false }) => {
  const [hoveredField, setHoveredField] = useState(null);
  const [editingField, setEditingField] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [showBboxes, setShowBboxes] = useState(true);
  const [error, setError] = useState('');
  const [currentExtraction, setCurrentExtraction] = useState(extraction);
  const imageRef = useRef(null);
  const canvasRef = useRef(null);
  const [editingKeyId, setEditingKeyId] = useState(null);
  const [editKeyValue, setEditKeyValue] = useState('');
  // Debug log
  console.log('ExtractionViewer - extraction:', extraction);
  console.log('ExtractionViewer - fields:', extraction?.fields);

  // Draw bounding boxes
  useEffect(() => {
    if (!currentExtraction || !imageRef.current || !canvasRef.current || !showBboxes) return;

    const canvas = canvasRef.current;
    const img = imageRef.current;
    const ctx = canvas.getContext('2d');

    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    if (!hoveredField) return;

    const pageDims = currentExtraction.page_dimensions?.['1'];
    if (!pageDims) return;

    const scaleX = canvas.width / (pageDims.image_width_px || canvas.width);
    const scaleY = canvas.height / (pageDims.image_height_px || canvas.height);

    const drawPoly = (polygon, fillColor, strokeColor) => {
      if (!polygon || polygon.length < 8) return;
      ctx.beginPath();
      ctx.moveTo(polygon[0] * scaleX, polygon[1] * scaleY);
      for (let i = 2; i < polygon.length; i += 2) {
        ctx.lineTo(polygon[i] * scaleX, polygon[i + 1] * scaleY);
      }
      ctx.closePath();
      ctx.fillStyle = fillColor;
      ctx.fill();
      ctx.strokeStyle = strokeColor;
      ctx.lineWidth = 2;
      ctx.stroke();
    };

    if (hoveredField.key_bbox?.polygon) {
      drawPoly(hoveredField.key_bbox.polygon, 'rgba(59, 130, 246, 0.3)', 'rgba(59, 130, 246, 0.8)');
    }

    if (hoveredField.value_bbox?.polygon) {
      drawPoly(hoveredField.value_bbox.polygon, 'rgba(34, 197, 94, 0.3)', 'rgba(34, 197, 94, 0.8)');
    }
  }, [hoveredField, currentExtraction, showBboxes]);

  const handleFieldEdit = (field) => {
    setEditingField(field.id);
    setEditValue(field.field_value || '');
  };

  const handleKeySave = async (field) => {
    try {
        await fetch(
            `${API_BASE}/extractions/${currentExtraction.id}/fields/${field.id}`,
            { 
                method: 'PATCH', 
                headers: { 'Content-Type': 'application/json', ...authHeader() },
                body: JSON.stringify({ field_key: editKeyValue })
            }
        );
        setCurrentExtraction(prev => ({
            ...prev,
            fields: prev.fields.map(f => 
                f.id === field.id ? { ...f, field_key: editKeyValue } : f
            )
        }));
        setEditingKeyId(null);
    } catch (err) {
        setError('Failed to update key');
    }
};

  const handleFieldSave = async (field) => {
    if (!currentExtraction) return;

    try {
      await API.updateField(currentExtraction.id, field.id, editValue);
      
      setCurrentExtraction({
        ...currentExtraction,
        fields: currentExtraction.fields.map(f => 
          f.id === field.id ? { ...f, field_value: editValue, is_edited: true } : f
        )
      });
      
      setEditingField(null);
    } catch (err) {
      setError('Failed to update field');
    }
  };

  const handleDelete = async (fieldId) => {
    try {
        await API.deleteField(currentExtraction.id, fieldId);
        setCurrentExtraction(prev => ({
            ...prev,
            fields: prev.fields.filter(f => f.id !== fieldId)
        }));
    } catch (err) {
        setError('Failed to delete field');
    }
};

  const handleSaveAll = async () => {
    if (!currentExtraction) return;

    setSaving(true);
    try {
      await API.saveExtraction(currentExtraction.id);
      alert('✅ Extraction saved to database successfully!');
      if (onBack) onBack();
    } catch (err) {
      setError('Failed to save extraction');
    } finally {
      setSaving(false);
    }
  };

  const fields = currentExtraction?.fields || [];
  const hasFields = fields.length > 0;

  return (
    <>
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2 mb-6">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-lg p-4 mb-6 flex items-center justify-between">
        <div className="flex items-center gap-4">
          {onBack && (
            <button
              onClick={onBack}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>
          )}
          
          <button
            onClick={() => setShowBboxes(!showBboxes)}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            {showBboxes ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            {showBboxes ? 'Hide' : 'Show'} Highlights
          </button>
          
          <div className="text-sm text-gray-600">
            {fields.length} fields extracted
          </div>
        </div>

        {showSaveButton && (
          <button
            onClick={handleSaveAll}
            disabled={saving || String(currentExtraction.status || '').toUpperCase() !== 'COMPLETED'}
            className="flex items-center gap-2 px-6 py-2 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50"
          >
            {saving ? (
              <>
                <Loader className="w-4 h-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Save to Database
              </>
            )}
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Document</h3>
          
          {currentExtraction.status === 'PROCESSING' ? (
            <div className="flex items-center justify-center h-96">
              <div className="text-center">
                <Loader className="w-12 h-12 text-indigo-600 animate-spin mx-auto mb-4" />
                <p className="text-gray-600">Processing document...</p>
              </div>
            </div>
          ) : (
            <div className="relative border border-gray-200 rounded-lg overflow-hidden">
              <img
                ref={imageRef}
                src={API.getProcessedImage(currentExtraction.document_id, 1)}
                alt="Document"
                className="w-full"
                crossOrigin="anonymous"
              />
              <canvas
                ref={canvasRef}
                className="absolute top-0 left-0 w-full h-full pointer-events-none"
                style={{ width: '100%', height: '100%' }}
              />
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl shadow-lg p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Extracted Data</h3>
          
          {!hasFields ? (
            <div className="text-center py-12 text-gray-500">
              <AlertCircle className="w-12 h-12 mx-auto mb-4 text-gray-400" />
              <p>No fields extracted yet</p>
            </div>
          ) : (
            <div className="space-y-3 max-h-[800px] overflow-y-auto">
              {fields.map((field) => (
                <div
                  key={field.id}
                  className={`p-4 border rounded-lg transition-all ${
                    hoveredField?.id === field.id
                      ? 'border-indigo-500 bg-indigo-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onMouseEnter={() => setHoveredField(field)}
                  onMouseLeave={() => setHoveredField(null)}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
    {editingKeyId === field.id && showSaveButton ? (
        <>
            <input
                type="text"
                value={editKeyValue}
                onChange={(e) => setEditKeyValue(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleKeySave(field)}
                className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500 outline-none"
                autoFocus
            />
            <button
                onClick={() => handleKeySave(field)}
                className="text-green-600 hover:text-green-700"
            >
                <CheckCircle className="w-4 h-4" />
            </button>
            <button
                onClick={() => setEditingKeyId(null)}
                className="text-gray-400 hover:text-gray-600 text-xs"
            >
                ✕
            </button>
        </>
    ) : (
        <>
            <label className="block text-sm font-medium text-gray-700">
                {field.field_key}
            </label>
            {showSaveButton && (
                <button
                    onClick={() => {
                        setEditingKeyId(field.id);
                        setEditKeyValue(field.field_key);
                    }}
                    className="text-gray-400 hover:text-indigo-600"
                    title="Edit key"
                >
                    <Edit className="w-3 h-3" />
                </button>
            )}
        </>
    )}
</div>
                      {editingField === field.id && showSaveButton ? (
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none"
                          />
                          <button
                            onClick={() => handleFieldSave(field)}
                            className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
                          >
                            <CheckCircle className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <div
                          onClick={() => showSaveButton && handleFieldEdit(field)}
                          className={`px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg ${
                            showSaveButton ? 'cursor-pointer hover:bg-gray-100' : ''
                          } transition-colors`}
                        >
                          {field.field_value || '(empty)'}
                        </div>
                      )}
                    </div>
			{showSaveButton && (
			    <button
			        onClick={() => handleDelete(field.id)}
			        className="ml-2 text-red-500 hover:text-red-700"
			    >
			        🗑 Delete
			    </button>
			)}
                  </div>

                  <div className="flex items-center gap-3 text-xs text-gray-500 flex-wrap">
                    <span className="px-2 py-1 bg-gray-100 rounded">
                      {field.field_type}
                    </span>
                    <span className={`px-2 py-1 rounded ${
                      field.confidence >= 0.85 
                        ? 'bg-green-100 text-green-700'
                        : field.confidence >= 0.6
                        ? 'bg-yellow-100 text-yellow-700'
                        : 'bg-red-100 text-red-700'
                    }`}>
                      {Math.round((field.confidence || 0) * 100)}% confidence
                    </span>
                    {field.is_edited && (
                      <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded">
                        Edited
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

// ============================================================================
// MAIN APP
// ============================================================================
const App = () => {
  const [user, setUser] = useState(AuthService.getUser());
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [documentId, setDocumentId] = useState(null);
  const [extraction, setExtraction] = useState(null);
  const [error, setError] = useState('');
  const [view, setView] = useState("upload");
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [selectedExtraction, setSelectedExtraction] = useState(null);
  const [documents, setDocuments] = useState([]);

  useEffect(() => {
    if (!AuthService.isAuthenticated()) {
      setUser(null);
    }
  }, []);

  // Poll extraction status
  useEffect(() => {
    if (!documentId || view !== "upload") return;
  
    const interval = setInterval(async () => {
      try {
        const ext = await API.pollExtractionStatus(documentId);
        
        if (ext) {
          console.log('Got extraction:', ext);
          setExtraction(ext);
          
          // ✅ Handle both string and enum object status
          const status = typeof ext.status === 'string' 
            ? ext.status.toUpperCase() 
            : ext.status.name || String(ext.status).split('.').pop();
          
          console.log('Normalized status:', status);
          
          if (status === 'COMPLETED') {
            console.log('✅ Extraction completed!');
            clearInterval(interval);
            setProcessing(false);
          } else if (status === 'FAILED') {
            console.log('❌ Extraction failed');
            clearInterval(interval);
            setProcessing(false);
            setError('Extraction failed. Please try again.');
          }
        }
      } catch (err) {
        console.error('Poll error:', err);
      }
    }, 2000);
  
    return () => clearInterval(interval);
  }, [documentId, view]);

  const handleFileSelect = (e) => {
    const selected = e.target.files[0];
    if (selected) {
      setFile(selected);
      setExtraction(null);
      setDocumentId(null);
      setError('');
    }
  };

  const handleUpload = async () => {
    if (!file) return;
  
    setUploading(true);
    setProcessing(true);
    setError('');
  
    try {
      const result = await API.uploadDocument(file);
      setDocumentId(result.id);
    } catch (err) {
      setError(err.message);
      setProcessing(false);
    } finally {
      setUploading(false);
    }
  };

  const handleNewUpload = () => {
    setFile(null);
    setExtraction(null);
    setDocumentId(null);
    setError('');
    setProcessing(false);
    setView("upload");
  };

  const handleDeleteDocument = async (documentId) => {
    try {
      await API.deleteDocument(documentId);
      
      // Remove from local state
      setDocuments(prev => prev.filter(d => d.id !== documentId));
      
      console.log('Document deleted successfully');
    } catch (err) {
      setError('Failed to delete document: ' + err.message);
    }
  };


  // const handleSelectDocument = async (doc) => {
  //   try {
  //     if (doc.current_extraction_id) {
  //       const ext = await API.getExtraction(doc.current_extraction_id);
  //       console.log('Loaded extraction from document:', ext); // Debug log
  //       setSelectedExtraction(ext);
  //     } else {
  //       setError('No extraction available for this document');
  //     }
  //   } catch (err) {
  //     setError('Failed to load extraction: ' + err.message);
  //   }
  // };

  const handleSelectDocument = async (doc) => {
    try {
      if (doc.current_extraction_id) {
        const ext = await API.getExtraction(doc.current_extraction_id);
        console.log('Loaded extraction from document:', ext);
        setSelectedExtraction(ext);
        setIsReadOnly(doc.is_finalized || false);
      } else {
        setError('No extraction available for this document');
      }
    } catch (err) {
      setError('Failed to load extraction: ' + err.message);
    }
  };

  const handleSelectSavedExtraction = (ext) => {
    console.log('Selected saved extraction:', ext); // Debug log
    setSelectedExtraction(ext);
  };

  const handleLogout = () => {
    AuthService.logout();
    setUser(null);
    setFile(null);
    setExtraction(null);
    setDocumentId(null);
    setView("upload");
  };

  if (!user) {
    return <AuthScreen onLogin={setUser} />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <FileText className="w-8 h-8 text-indigo-600" />
            <h1 className="text-2xl font-bold text-gray-800">InvoSense AI</h1>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                setView("upload");
                setSelectedExtraction(null);
              }}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                view === "upload" && !selectedExtraction
                  ? 'bg-indigo-600 text-white'
                  : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
              }`}
            >
              <Home className="w-4 h-4" />
              Home
            </button>

            <button
              onClick={() => {
                setView("processed");
                setSelectedExtraction(null);
              }}
              className={`px-4 py-2 rounded-lg transition-colors ${
                view === "processed" && !selectedExtraction
                  ? 'bg-indigo-600 text-white'
                  : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
              }`}
            >
              Processed
            </button>

            <button
              onClick={() => {
                setView("saved");
                setSelectedExtraction(null);
              }}
              className={`px-4 py-2 rounded-lg transition-colors ${
                view === "saved" && !selectedExtraction
                  ? 'bg-indigo-600 text-white'
                  : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
              }`}
            >
              Saved
            </button>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <User className="w-4 h-4" />
              <span>{user.email}</span>
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <LogOut className="w-4 h-4" />
              Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Upload View */}
        {view === "upload" && !extraction && (
          <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Upload Document</h2>
            
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-12 text-center hover:border-indigo-400 transition-colors">
              <input
                type="file"
                id="file-upload"
                accept=".png,.jpg,.jpeg,.pdf"
                onChange={handleFileSelect}
                className="hidden"
              />
              
              <label htmlFor="file-upload" className="cursor-pointer block">
                <Upload className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <p className="text-lg text-gray-700 mb-2">
                  {file ? file.name : 'Click to upload or drag and drop'}
                </p>
                <p className="text-sm text-gray-500">PNG, JPG, JPEG or PDF (max 20MB)</p>
              </label>
            </div>

            {file && (
              <button
                onClick={handleUpload}
                disabled={uploading || processing}
                className="mt-6 w-full bg-indigo-600 hover:bg-indigo-700 text-white font-medium py-3 rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {(uploading || processing) ? (
                  <>
                    <Loader className="w-5 h-5 animate-spin" />
                    Extracting Data...
                  </>
                ) : (
                  <>
                    <Upload className="w-5 h-5" />
                    Extract Data
                  </>
                )}
              </button>
            )}
          </div>
        )}

        {/* Extraction View */}
        {view === "upload" && extraction && (
          <>
            <div className="mb-6">
              <button
                onClick={handleNewUpload}
                className="flex items-center gap-2 px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-lg transition-colors"
              >
                <Upload className="w-5 h-5" />
                Upload New Document
              </button>
            </div>
            <ExtractionViewer 
              extraction={extraction} 
              showSaveButton={true}
              onBack={null}
            />
          </>
        )}

        {/* Processed View */}
        {view === "processed" && !selectedExtraction && (
          <ProcessedView onSelectDocument={handleSelectDocument}
          onDeleteDocument={handleDeleteDocument} 
           />
        )}

        {/* Saved View */}
        {view === "saved" && !selectedExtraction && (
          <SavedView onSelectExtraction={handleSelectSavedExtraction} />
        )}

        {/* Selected Extraction View */}
        {/* Selected Extraction View - Handles both Processed and Saved */}
        {(view === "processed" || view === "saved") && selectedExtraction && (
          <ExtractionViewer 
            extraction={selectedExtraction}
            showSaveButton={view === "processed" && !isReadOnly}  // ✅ Only show save button in processed view if not finalized
            readOnly={view === "saved" || isReadOnly}  // ✅ Read-only for saved view OR finalized extractions
            onBack={() => {
              setSelectedExtraction(null);
              setIsReadOnly(false);
            }}
          />
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2 mb-6">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}
      </main>
    </div>
  );
};

export default App;
